// Exact decimal helpers for frontend money arithmetic.
// Values are kept as decimal strings and converted to Number only at display boundaries.
export function money(value) {
  if (value === null || value === undefined || value === "") return "0";
  const s = String(value).trim().replace(/,/g, "");
  if (!/^-?\d+(?:\.\d+)?$/.test(s)) return "0";
  return s;
}

function parts(value) {
  const s = money(value);
  const neg = s.startsWith("-");
  const raw = neg ? s.slice(1) : s;
  const [whole, frac = ""] = raw.split(".");
  const scale = frac.length;
  return { neg, whole: whole || "0", frac, scale };
}

export function moneyCents(value) {
  const p = parts(value);
  const scale = 6;
  const raw = BigInt(p.whole) * (10n ** BigInt(scale)) + BigInt((p.frac + "0".repeat(scale)).slice(0, scale) || "0");
  return p.neg ? -raw : raw;
}

function fromScaled(n, scale) {
  const neg = n < 0n;
  const a = (neg ? -n : n).toString().padStart(scale + 1, "0");
  const whole = a.slice(0, -scale) || "0";
  const frac = a.slice(-scale).replace(/0+$/, "");
  return (neg ? "-" : "") + whole + (frac ? "." + frac : "");
}

export function moneyAdd(...values) {
  const scale = Math.max(2, ...values.map(v => parts(v).scale));
  const total = values.reduce((sum, value) => {
    const p = parts(value);
    const raw = BigInt(p.whole) * (10n ** BigInt(scale)) + BigInt((p.frac + "0".repeat(scale)).slice(0, scale) || "0");
    return sum + (p.neg ? -raw : raw);
  }, 0n);
  return fromScaled(total, scale);
}

export function moneySub(a, b) {
  const value = money(b);
  return moneyAdd(a, value.startsWith("-") ? moneyAbs(value) : "-" + value);
}

export function moneyMul(a, quantity) {
  const q = Number(quantity || 0);
  if (!Number.isSafeInteger(q)) return "0";
  const p = parts(a);
  const scale = Math.max(2, p.scale);
  const raw = BigInt(p.whole) * (10n ** BigInt(scale)) + BigInt((p.frac + "0".repeat(scale)).slice(0, scale) || "0");
  return fromScaled((p.neg ? -raw : raw) * BigInt(q), scale);
}

export function moneyDiv(a, divisor) {
  const d = Number(divisor);
  if (!Number.isSafeInteger(d) || d === 0) return "0";
  const p = parts(a);
  const scale = Math.max(6, p.scale);
  const raw = BigInt(p.whole) * (10n ** BigInt(scale)) + BigInt((p.frac + "0".repeat(scale)).slice(0, scale) || "0");
  const signed = p.neg ? -raw : raw;
  return fromScaled(signed / BigInt(d), scale);
}

export function moneyAbs(value) {
  const s = money(value);
  return s.startsWith("-") ? s.slice(1) : s;
}

export function moneyMax(a, b) {
  return moneyCents(a) >= moneyCents(b) ? money(a) : money(b);
}

export function moneyRatioPercent(numerator, denominator) {
  const den = moneyCents(denominator);
  if (den === 0n) return 0;
  return Number(money(numerator)) / Number(money(denominator)) * 100;
}
